import { useQuery } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { workspacesApi } from '../api/workspaces'
import type { WorkspaceMemberInfo } from '../types/api'

/** Full role set, shown in the role picker for an existing member. */
export const MEMBER_ROLES = ['owner', 'admin', 'member', 'viewer'] as const

/** Roles a member can be added/invited as (owner is transfer-only, not assignable). */
export const ASSIGNABLE_ROLES = ['admin', 'member', 'viewer'] as const

/** Canonical cache key for a workspace's members. */
export function membersKey(workspaceId: string) {
  return ['workspace-members', workspaceId] as const
}

/** Whether a failed role update is the server refusing a change to your own role. */
export function isOwnRoleRefusal(
  error: Error,
  targetUserId: string,
  currentUserId: string | null | undefined,
) {
  return (
    error instanceof ApiError &&
    error.status === 422 &&
    targetUserId === currentUserId
  )
}

/**
 * A workspace's members, under one canonical key so the admin card and the
 * settings tab share a cache entry (they previously keyed it two different
 * ways, so a change in one view didn't refresh the other). `opts` passes
 * through the per-call config that legitimately differs (admin fetches lazily
 * on expand; settings uses a staleTime).
 */
export function useWorkspaceMembersQuery(
  workspaceId: string,
  opts?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery<WorkspaceMemberInfo[]>({
    queryKey: membersKey(workspaceId),
    queryFn: () => workspacesApi.listMembers(workspaceId),
    ...opts,
  })
}
